import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2, ExternalLink, BellRing, Unplug } from "lucide-react";

export const TelegramConnect = () => {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => api.telegramStatus().then(setStatus).catch(() => {});
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!token.trim()) { toast.error("Paste your bot token from @BotFather"); return; }
    setBusy(true);
    try {
      const r = await api.telegramConnect(token.trim());
      toast.success(`Connected @${r.bot_username}`);
      setToken(""); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not connect"); }
    finally { setBusy(false); }
  };

  const disconnect = async () => { setBusy(true); try { await api.telegramDisconnect(); toast.success("Telegram disconnected"); load(); } catch { } finally { setBusy(false); } };
  const test = async () => { setBusy(true); try { const r = await api.telegramTest(); toast.success(r.message || "Sent"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); } finally { setBusy(false); } };

  const connected = status?.connected;

  return (
    <div data-testid="telegram-connect-card" className={`rounded-xl border p-4 mb-4 ${connected ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-cyan-500/30 bg-cyan-500/[0.04]"}`}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg grid place-items-center bg-[#229ED9]">
          <Send size={20} className="text-white -rotate-12" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-tactical font-bold text-sm text-zinc-100 flex items-center gap-2">Telegram
            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-zinc-400">two-way · free</span>
          </div>
          {connected ? (
            <div className="text-[11px] font-mono text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12} /> Connected · @{status.bot_username}{status.owner_ready ? " · ready to message you" : ""}</div>
          ) : (
            <div className="text-[11px] font-mono text-zinc-400">Message contacts here and let agents text you updates — via a free Telegram bot.</div>
          )}
        </div>
        {connected ? (
          <button data-testid="telegram-disconnect" onClick={disconnect} disabled={busy} className="flex items-center gap-1 text-[11px] font-mono text-zinc-400 border border-white/10 rounded-lg px-2.5 py-1.5 hover:bg-white/10"><Unplug size={12} /> Disconnect</button>
        ) : (
          <button data-testid="telegram-connect-toggle" onClick={() => setOpen((v) => !v)} className="text-xs font-tactical font-bold uppercase bg-cyan-500 hover:bg-cyan-400 text-[#04070c] rounded-lg px-3 py-2">{open ? "Close" : "Connect"}</button>
        )}
      </div>

      {connected && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a data-testid="telegram-open-bot" href={status.deep_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] font-mono text-cyan-300 border border-cyan-400/30 rounded-lg px-2.5 py-1.5 hover:bg-cyan-500/15"><ExternalLink size={12} /> Open @{status.bot_username} & press Start</a>
          <button data-testid="telegram-test" onClick={test} disabled={busy} className="flex items-center gap-1 text-[11px] font-mono text-emerald-300 border border-emerald-400/30 rounded-lg px-2.5 py-1.5 hover:bg-emerald-500/15">{busy ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />} Send me a test</button>
          {!status.owner_ready && <span className="text-[10px] text-amber-300/90">Press Start in your bot once so it can message you.</span>}
        </div>
      )}

      {open && !connected && (
        <div data-testid="telegram-setup" className="mt-3 border-t border-white/10 pt-3">
          <ol className="text-[12px] text-zinc-300 space-y-1.5 mb-3 list-decimal list-inside">
            <li>On Telegram, open <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">@BotFather</a> and send <code className="text-cyan-200">/newbot</code>.</li>
            <li>Pick a name and a username ending in <span className="text-cyan-200">bot</span>.</li>
            <li>BotFather replies with a <span className="text-cyan-200">token</span> like <code className="text-zinc-400">1234:AA…</code> — copy it.</li>
            <li>Paste it below and hit Connect. Then open your new bot and press <span className="text-cyan-200">Start</span>.</li>
          </ol>
          <div className="flex items-center gap-2">
            <input
              data-testid="telegram-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste bot token from @BotFather"
              className="flex-1 bg-[#05090f] border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
            />
            <button data-testid="telegram-connect-submit" onClick={connect} disabled={busy} className="h-9 px-4 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-bold text-sm flex items-center gap-1">{busy ? <Loader2 size={14} className="animate-spin" /> : "Connect"}</button>
          </div>
        </div>
      )}
    </div>
  );
};
